import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { getUserFacingMessage } from "../../api/errors";
import { apiGet } from "../../api/http";
import { loadDataStoreFromApi } from "../../api/loadDataStore";
import { fetchMemberLevelsForTenant } from "../../api/memberLevels";
import { fetchExclusiveBenefitsForTenant } from "../../api/exclusiveBenefits";
import { fetchActivityCategoriesForTenant } from "../../api/activityCategories";
import { fetchContributionCategoriesForTenant } from "../../api/contributionCategories";
import { fetchOrgStructureJson } from "../../api/orgStructure";
import { fetchAgreementDocuments } from "../../api/agreementDocuments";
import { assocBasicInfoFromApi, fetchTenantProfile } from "../../api/tenantProfile";
import type { MemberRow, PendingMemberDto, RegistrationRow } from "../../api/types";
import { basicInfoStore } from "./utils/basicInfoStore";
import { memberLevelStore } from "./utils/memberLevelStore";
import { documentStore } from "./utils/documentStore";
import { mergeSavedAgreementDocuments } from "./utils/defaultAgreementDocuments";
import { benefitsStore } from "./utils/benefitsStore";
import { activityCategoryStore } from "./utils/activityCategoryStore";
import { contributionStore } from "./utils/contributionStore";
import { orgStructureStore } from "./utils/orgStructureStore";

export type AdminReloadOptions = {
  /**
   * true：不展示主内容区「正在加载…」条（仍可在顶栏显示轻量刷新中）。
   * 默认：首次进入后台为 false，之后为 true，避免每次保存后整页像初次加载。
   */
  silent?: boolean;
};

type AdminDataContextValue = {
  tenantLegacyId: string;
  loading: boolean;
  /** 静默刷新时顶栏等可用的小菊花 */
  refreshing: boolean;
  error: string | null;
  members: MemberRow[];
  memberApplications: PendingMemberDto[];
  registrations: RegistrationRow[];
  /** dataStore 与 registrations 刷新后递增，供 ActivityManager 等同步本地 state */
  storeVersion: number;
  reload: (opts?: AdminReloadOptions) => Promise<void>;
};

const AdminDataContext = createContext<AdminDataContextValue | null>(null);

export function AdminDataProvider({
  tenantLegacyId,
  children,
}: {
  tenantLegacyId: string;
  children: ReactNode;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [memberApplications, setMemberApplications] = useState<PendingMemberDto[]>([]);
  const [registrations, setRegistrations] = useState<RegistrationRow[]>([]);
  const [storeVersion, setStoreVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const hasCompletedInitialLoadRef = useRef(false);
  const reloadGenerationRef = useRef(0);
  const prevTenantLegacyIdRef = useRef<string | null>(null);

  const reload = useCallback(async (opts?: AdminReloadOptions) => {
    const silent = opts?.silent ?? hasCompletedInitialLoadRef.current;
    const runId = ++reloadGenerationRef.current;

    if (!silent) {
      setLoading(true);
      setError(null);
    } else {
      setRefreshing(true);
    }

    try {
      const [m, a, r] = await Promise.all([
        apiGet<MemberRow[]>(`/api/tenants/${tenantLegacyId}/members`),
        apiGet<PendingMemberDto[]>(`/api/tenants/${tenantLegacyId}/member-applications?reviewStatus=pending`),
        apiGet<RegistrationRow[]>(`/api/tenants/${tenantLegacyId}/activity-registrations`),
      ]);

      if (runId !== reloadGenerationRef.current) return;

      setMembers(m);
      setMemberApplications(a);
      setRegistrations(r);

      await Promise.allSettled([
        (async () => {
          try {
            const profile = await fetchTenantProfile(tenantLegacyId);
            basicInfoStore.save(assocBasicInfoFromApi(profile));
          } catch {
            /* 档案失败时保留 basicInfoStore 现有内容（如本地曾保存） */
          }
        })(),
        (async () => {
          try {
            const agreeRows = await fetchAgreementDocuments(tenantLegacyId);
            const merged = mergeSavedAgreementDocuments(agreeRows);
            documentStore.syncFromSettings(
              merged.map((d) => ({
                id: d.id,
                title: d.title,
                type: d.type,
                requireSign: d.requireSign,
                version: d.version,
                isActive: d.isActive,
              })),
            );
          } catch {
            /* 文书接口失败时保留 documentStore 现有内容 */
          }
        })(),
        loadDataStoreFromApi(tenantLegacyId),
        (async () => {
          try {
            const ml = await fetchMemberLevelsForTenant(tenantLegacyId);
            memberLevelStore.replaceFromApi(ml);
          } catch {
            memberLevelStore.replaceFromApi([]);
          }
        })(),
        (async () => {
          try {
            const eb = await fetchExclusiveBenefitsForTenant(tenantLegacyId);
            benefitsStore.replaceFromApi(eb);
          } catch {
            benefitsStore.replaceFromApi([]);
          }
        })(),
        (async () => {
          try {
            const ac = await fetchActivityCategoriesForTenant(tenantLegacyId);
            activityCategoryStore.replaceFromApi(ac);
          } catch {
            activityCategoryStore.replaceFromApi([]);
          }
        })(),
        (async () => {
          try {
            const cc = await fetchContributionCategoriesForTenant(tenantLegacyId);
            contributionStore.replaceFromApi(cc);
          } catch {
            contributionStore.replaceFromApi([]);
          }
        })(),
        (async () => {
          try {
            const orgRaw = await fetchOrgStructureJson(tenantLegacyId);
            orgStructureStore.replaceFromApi(orgRaw);
          } catch {
            orgStructureStore.replaceFromApi("[]");
          }
        })(),
      ]);

      if (runId !== reloadGenerationRef.current) return;

      setStoreVersion((v) => v + 1);
    } catch (e) {
      if (runId === reloadGenerationRef.current) {
        setError(getUserFacingMessage(e, "协会管理数据加载失败，请刷新页面。"));
      }
    } finally {
      if (runId === reloadGenerationRef.current) {
        if (!silent) setLoading(false);
        setRefreshing(false);
        hasCompletedInitialLoadRef.current = true;
      }
    }
  }, [tenantLegacyId]);

  useEffect(() => {
    const prev = prevTenantLegacyIdRef.current;
    prevTenantLegacyIdRef.current = tenantLegacyId;
    if (prev !== null && prev !== tenantLegacyId) {
      hasCompletedInitialLoadRef.current = false;
      reloadGenerationRef.current += 1;
      setLoading(false);
      setRefreshing(false);
    }
  }, [tenantLegacyId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const value = useMemo(
    () => ({
      tenantLegacyId,
      loading,
      refreshing,
      error,
      members,
      memberApplications,
      registrations,
      storeVersion,
      reload,
    }),
    [
      tenantLegacyId,
      loading,
      refreshing,
      error,
      members,
      memberApplications,
      registrations,
      storeVersion,
      reload,
    ]
  );

  return <AdminDataContext.Provider value={value}>{children}</AdminDataContext.Provider>;
}

export function useAdminData(): AdminDataContextValue {
  const ctx = useContext(AdminDataContext);
  if (!ctx) throw new Error("useAdminData must be used within AdminDataProvider");
  return ctx;
}
